'use client';

import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useState } from 'react';
import { ArrowRight, Basket as ShoppingBasket, Clock, MagnifyingGlass as Search } from '@phosphor-icons/react/dist/ssr';
import type { CategoryData, MenuItemData } from '@/types/public-menu';
import { ProductSearchBox } from '../ProductSearchBox';
import { sized } from '../grocery-utils';
import { getContextualHref } from '@/lib/domains';

export function HomeHero({ slug, name, description, logo, status, valueLine, ordering, collage, aisles, sym }: {
  slug: string;
  name: string;
  description?: string;
  logo?: string;
  status?: { open: boolean; label: string } | null;
  valueLine: string[];
  ordering: boolean;
  collage: MenuItemData[];
  aisles: CategoryData[];
  sym: string;
}) {
  const router = useRouter();
  const [q, setQ] = useState('');
  const menuPath = getContextualHref(slug, '/menu');
  const search = () => {
    router.push(q.trim() ? `${menuPath}?q=${encodeURIComponent(q.trim())}` : menuPath);
  };

  return (
    <header className="gh-hero">
      <div className="gh-wrap gh-hero-grid">
        <div className="gh-rise">
          {status && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '0.8rem', fontWeight: 700, color: status.open ? 'var(--color-primary)' : '#b91c1c' }}>
              <Clock size={14} aria-hidden /> {status.label}
            </span>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginTop: 10 }}>
            {logo && <img src={sized(logo, 128)} alt="" width={52} height={52} style={{ width: 52, height: 52, borderRadius: 14, objectFit: 'cover', border: '1px solid var(--color-border)' }} />}
            <h1 className="gh-display">{name}</h1>
          </div>
          {valueLine.length > 0 ? (
            <p className="gh-lead">{valueLine.join(' · ')}</p>
          ) : description ? <p className="gh-lead">{description.slice(0, 140)}</p> : null}

          <div style={{ marginTop: 22, display: 'flex', gap: 8, maxWidth: 720, alignItems: 'flex-start' }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <ProductSearchBox slug={slug} categories={aisles} value={q} onChange={setQ} onSubmit={() => search()} currencySymbol={sym} placeholder="Search products, brands or aisles" />
            </div>
            <button type="button" onClick={() => search()} className="gh-btn gh-btn-ghost">Search</button>
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 16, marginTop: 18 }}>
            <Link href={menuPath} className="gh-btn">
              <ShoppingBasket size={18} aria-hidden /> {ordering ? 'Shop all products' : 'Browse all products'} <ArrowRight size={16} aria-hidden />
            </Link>
            <Link href={getContextualHref(slug, '/lists')} className="gh-link">Your lists &amp; order again</Link>
            {ordering && <Link href={getContextualHref(slug, '/track')} className="gh-link">Track your order</Link>}
          </div>
        </div>

        {collage.length >= 3 && (
          <div className="gh-collage" aria-hidden>
            {collage.slice(0, 4).map((p, i) => (
              <div key={p.id} className={`gh-collage-${i}`}>
                <img src={sized(p.imageUrl, 480)} alt="" decoding="async" fetchPriority={i === 0 ? 'high' : undefined} />
              </div>
            ))}
          </div>
        )}
      </div>
    </header>
  );
}
